import { withDevtools, withSessionStorage, withStorageSync } from '@angular-architects/ngrx-toolkit';
import { computed, inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { I18NextPipe } from 'angular-i18next';
import { isEqual } from 'lodash';
import { ToastService } from 'mbs-ui-kit';
import { ComputersHealthFilterType } from 'mbs-ui-shared@models/Computer';
import {
  RmmComputersStatistic,
  RmmPageFields,
  RmmPageFilterItemField,
  RmmPageQuery,
  RmmPageQueryPayload,
  RmmPageTemplate,
  RmmViewColumnData
} from 'mbs-ui-shared@models/rmm-warehouse.models';
import { RmmComputersService } from 'mbs-ui-shared@services/rmm-computers.service';
import { EMPTY, mergeMap, pipe, switchMap } from 'rxjs';

import { splitRmmPageFilter, toRmmPageQueryFilter } from './rmm-warehouse-filter';

interface RmmWarehouseStoreState {
  presets: RmmPageTemplate[];
  presetId: string;
  fields: RmmPageFields[];
  query: RmmPageQuery;
  uiState: RmmViewColumnData[];
  data: any[];
  page: number;
  pageSize: number;
  total: number;
  searchConditions: RmmPageFilterItemField[];
  statisticFilter: ComputersHealthFilterType;
  searchTotal: number | null;
  statistic: RmmComputersStatistic | null;
  statisticLoading: boolean;
  loading: boolean;
  dataLoading: boolean;
}

const initialState: RmmWarehouseStoreState = {
  fields: [],
  presets: [],
  presetId: null,
  query: {
    projection: [],
    orderBy: null,
    filter: null
  },
  data: [],
  uiState: null,
  page: 1,
  pageSize: 50,
  total: 0,
  searchConditions: [],
  statisticFilter: ComputersHealthFilterType.All,
  searchTotal: null,
  statistic: null,
  statisticLoading: false,
  loading: false,
  dataLoading: false
};

export const RmmWarehouseStore = signalStore(
  { providedIn: 'root' },
  withDevtools('RmmWarehouseStore'),
  withState(initialState),
  withStorageSync(
    {
      key: 'rmm-page-state',
      select: ({ query, uiState, searchConditions, statisticFilter }) => ({ query, uiState, searchConditions, statisticFilter })
    },
    withSessionStorage()
  ),
  withComputed((store) => ({
    params: computed(() => ({ query: store.query(), uiState: store.uiState(), page: store.page(), pageSize: store.pageSize() })),
    sortableFields: computed(() => store.fields().filter((field) => field.sortSupported)),
    preset: computed(() => store.presets().find((preset) => preset.id === store.presetId()) ?? null)
  })),
  withMethods((store) => {
    const service = inject(RmmComputersService);
    const toastService = inject(ToastService);
    const i18n = inject(I18NextPipe);

    const loadStatistic = rxMethod<void>(
      pipe(
        switchMap(() => {
          patchState(store, { statisticLoading: true });

          return service.getStatistic(toRmmPageQueryFilter(store.searchConditions())).pipe(
            tapResponse({
              next: (statistic) => patchState(store, { statistic, statisticLoading: false }),
              error: () => patchState(store, { statistic: null, statisticLoading: false })
            })
          );
        })
      )
    );

    const setFilters = (searchConditions: RmmPageFilterItemField[], statisticFilter: ComputersHealthFilterType): void => {
      const searchChanged = !isEqual(searchConditions, store.searchConditions());

      patchState(store, {
        searchConditions,
        statisticFilter,
        ...(searchChanged && statisticFilter !== ComputersHealthFilterType.All && { searchTotal: null })
      });

      if (searchChanged) loadStatistic();
    };

    const applyPresetQuery = (preset: RmmPageTemplate): void => {
      const { searchConditions, statisticFilter } = splitRmmPageFilter(
        (preset.query?.filter?.conditions ?? []) as RmmPageFilterItemField[]
      );

      patchState(store, { query: preset.query });
      setFilters(searchConditions, statisticFilter);
    };

    const changePreset = (preset: RmmPageTemplate): void => {
      applyPresetQuery(preset);
      patchState(store, { uiState: preset.uiState ?? null, presetId: preset.id });
    };

    const failViewLoad = (presetId: string) => {
      patchState(store, { presetId, data: [], total: 0, statistic: null, searchTotal: null });
      toastService.error(i18n.transform('rmm-page:saveView.loadViewError'));
    };

    const loadData = rxMethod<{ presetId: string | null }>(
      pipe(
        switchMap(({ presetId }) => {
          const preset = presetId ? store.presets().find((item) => item.id === presetId) : null;

          patchState(store, { presetId, dataLoading: true });

          if (preset) {
            applyPresetQuery(preset);
            patchState(store, { uiState: preset.uiState ?? null });
          }

          const request$ = preset
            ? service.getDataFromTemplate(presetId, { page: store.page(), pageSize: store.pageSize() })
            : service.getData(store.params());

          return request$.pipe(
            tapResponse({
              next: (data) =>
                patchState(store, (state) => ({
                  dataLoading: false,
                  data: data.items,
                  total: data.total,
                  searchTotal: state.statisticFilter === ComputersHealthFilterType.All ? data.total : state.searchTotal
                })),
              error: () => {
                patchState(store, { dataLoading: false, data: [], total: 0 });
                toastService.error(i18n.transform('rmm-page:page.loadDataError'));
              }
            })
          );
        })
      )
    );

    return {
      loadStatistic,
      setFilters,
      changePreset,
      loadData,
      updateQuery(query: Partial<RmmPageQuery>) {
        patchState(store, (state) => ({ query: { ...state.query, ...query } }));
      },
      updateUIState(uiState: RmmViewColumnData[]) {
        patchState(store, { uiState });
      },
      updatePaging(page: number, pageSize: number) {
        patchState(store, { page, pageSize });
      },
      clearPreset() {
        patchState(store, { presetId: null });
      },
      loadFields: rxMethod<void>(
        pipe(
          mergeMap(() => {
            if (store.fields().length) return EMPTY;

            patchState(store, { loading: true });

            return service.getAvailableFields().pipe(
              tapResponse({
                next: (res) => patchState(store, { loading: false, fields: res.fields }),
                error: () => patchState(store, { loading: false, fields: [] })
              })
            );
          })
        )
      ),
      savePreset: rxMethod<{ payload: RmmPageQueryPayload }>(
        pipe(
          mergeMap(({ payload }) => {
            patchState(store, { loading: true });

            return service.savePreset(payload).pipe(
              tapResponse({
                next: (data) => patchState(store, { loading: false, presets: store.presets().concat(data), presetId: data.id }),
                error: () => patchState(store, { loading: false })
              })
            );
          })
        )
      ),
      updatePreset: rxMethod<{ id: string; payload: RmmPageQueryPayload }>(
        pipe(
          mergeMap(({ id, payload }) => {
            patchState(store, { loading: true });

            return service.updatePreset(id, payload).pipe(
              tapResponse({
                next: (data) =>
                  patchState(store, {
                    loading: false,
                    presets: store.presets().map((preset) => (preset.id === id ? data : preset)),
                    presetId: id
                  }),
                error: () => patchState(store, { loading: false })
              })
            );
          })
        )
      ),
      removePreset: rxMethod<{ id: string }>(
        pipe(
          mergeMap(({ id }) => {
            patchState(store, { loading: true });

            return service.deletePreset(id).pipe(
              tapResponse({
                next: () =>
                  patchState(store, (state) => ({
                    loading: false,
                    presets: state.presets.filter((preset) => preset.id !== id),
                    presetId: state.presetId === id ? null : state.presetId
                  })),
                error: () => patchState(store, { loading: false })
              })
            );
          })
        )
      ),
      loadPresets: rxMethod<{ presetId?: string }>(
        pipe(
          switchMap(({ presetId }) => {
            if (store.presets().length && !presetId) return EMPTY;

            patchState(store, { loading: true });

            return service.getPresets().pipe(
              tapResponse({
                next: (presets) => {
                  const presetToSet = presetId ? presets.find((item) => item.id === presetId) : null;

                  patchState(store, { loading: false, presets });

                  if (presetToSet) {
                    changePreset(presetToSet);
                    loadData({ presetId });
                  } else if (presetId) {
                    failViewLoad(presetId);
                  }
                },
                error: () => {
                  patchState(store, { loading: false, presets: [] });
                  if (presetId) failViewLoad(presetId);
                }
              })
            );
          })
        )
      )
    };
  })
);

export type RmmWarehouseStore = InstanceType<typeof RmmWarehouseStore>;
